// 📄 pages/MiEspacio/MiEspacioHome.tsx
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserProjects } from '../../context/ProjectsContext.tsx';
import MiEspacioTabs from './MiEspacioTabs.tsx';
import MiEspacioPinGate from './components/MiEspacioPinGate.tsx';

const FILTRO_TODOS = 'todos';
const FILTRO_GLOBAL = 'global';

export default function MiEspacioHome() {
  const { proyectos } = useUserProjects();
  const [filtro, setFiltro] = useState<string>(FILTRO_TODOS);
  // Acceso directo desde el Home (chip "Buscar con IA", ver
  // HomeLanding.tsx) — abre esta pantalla ya parado en esa pestaña en vez
  // de arrancar siempre en Snippets. Se lee una sola vez al montar
  // (mismo criterio que un valor inicial de useState); cambiar de pestaña
  // después ya lo maneja MiEspacioTabs internamente, sin tocar la URL.
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab') || undefined;

  const proyectosPorId = useMemo(
    () => Object.fromEntries(proyectos.map((p) => [p.id, p.nombre])),
    [proyectos]
  );

  // "Todos" consulta el espacio Global (null) + cada proyecto del usuario;
  // "Global" solo el espacio fuera de proyecto; un proyecto puntual, solo
  // ese contexto — ver miEspacioContexto.ts.
  const contextosConsulta = useMemo<(string | null)[]>(() => {
    if (filtro === FILTRO_TODOS) return [null, ...proyectos.map((p) => p.id)];
    if (filtro === FILTRO_GLOBAL) return [null];
    return [filtro];
  }, [filtro, proyectos]);

  // Un item NUEVO creado desde el Home se guarda en el contexto que esté
  // filtrado, salvo que el filtro mezcle varios ("Todos") — ahí no hay un
  // único destino obvio, así que cae al espacio Global por defecto.
  const contextoNuevo = filtro === FILTRO_TODOS || filtro === FILTRO_GLOBAL ? null : filtro;

  // El badge de proyecto en cada tarjeta solo aporta cuando se mezclan
  // contextos — con un filtro puntual (Global o un proyecto específico)
  // sería redundante, ya se sabe de dónde es todo lo que se ve.
  const mostrarProyecto = filtro === FILTRO_TODOS;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Mi Espacio</h1>
          <p className="text-sm text-gray-400 mt-1">
            Tu espacio personal para guardar código Z, notas técnicas, soluciones a errores de SAP y directorio de contactos.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-400 whitespace-nowrap">Contexto:</label>
          <select
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
          >
            <option value={FILTRO_TODOS}>Todos los Proyectos</option>
            <option value={FILTRO_GLOBAL}>Solo Global / Freelance</option>
            {proyectos.map((p) => (
              <option key={p.id} value={p.id}>Proyecto: {p.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <MiEspacioPinGate>
        <MiEspacioTabs
          contextosConsulta={contextosConsulta}
          contextoNuevo={contextoNuevo}
          proyectosPorId={proyectosPorId}
          mostrarProyecto={mostrarProyecto}
          tabInicial={tabInicial}
        />
      </MiEspacioPinGate>
    </div>
  );
}
